import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { alpha, styled } from '@mui/material/styles'
import Security from '@mui/icons-material/Security'
import Assignment from '@mui/icons-material/Assignment'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Business from '@mui/icons-material/Business'
import Edit from '@mui/icons-material/Edit'
import FactCheck from '@mui/icons-material/FactCheck'
import LocationOn from '@mui/icons-material/LocationOn'
import ManageAccounts from '@mui/icons-material/ManageAccounts'
import HowToReg from '@mui/icons-material/HowToReg'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import Person from '@mui/icons-material/Person'
import Refresh from '@mui/icons-material/Refresh'
import Save from '@mui/icons-material/Save'
import Close from '@mui/icons-material/Close'
import Search from '@mui/icons-material/Search'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  attachOutgoingAuditor,
  detachOutgoingAuditor,
  getAuditableTargets,
  getPractitionerAuditBootstrap,
  getPractitionerAuditDetail,
  getPractitionerAuditRooms,
  savePractitionerRoomAuditors,
} from 'src/supportPortal/services/practitionerOnboardingService'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getUserRoles } from 'src/mentor/layout/utils'
import { toast } from 'react-toastify'
import AuditRequestsListing from './AuditRequestsListing'
import AuditorAssignmentForm from 'src/supportPortal/views/serviceApprovals/components/AuditorAssignmentForm'

const ROLE_AUDIT_ADMIN = 'ROLE_AUDIT_ADMIN'
const ROLE_THERAPIST_AUDIT_ADMIN = 'ROLE_THERAPIST_AUDIT_ADMIN'
const ROLE_SCREENER_AUDIT_ADMIN = 'ROLE_SCREENER_AUDIT_ADMIN'
const ROLE_ADMIN = 'ROLE_ADMIN'
const ROLE_THERAPIST = 'ROLE_THERAPIST'
const ROLE_SCREENER = 'ROLE_SCREENER'
const ROLE_THERAPIST_ADMIN = 'ROLE_THERAPIST_ADMIN'
const ROLE_THERAPY_AUDITOR = 'ROLE_THERAPY_AUDITOR'
const ROLE_SCREENING_AUDITOR = 'ROLE_SCREENING_AUDITOR'
const ASSIGN_AUDITOR_SECTION = 'assign-auditor-role'
const AUDITOR_PROFILE_SECTIONS = ['audit-requests', 'auditors', 'auditing-permissions']
const COVERAGE_PAGE_SIZE = 20
const AUDITOR_ROLE_LABELS = {
  ROLE_THERAPY_AUDITOR: 'Therapy Auditor',
  ROLE_SCREENING_AUDITOR: 'Screening Auditor',
}
const EVENT_TYPE_THERAPY_SESSION = 'therapy-session'
const EVENT_TYPE_SCREENING = 'screening'
const AUDITOR_TYPE_OPTIONS = [
  { id: 'SUPERVISOR', label: 'Supervisor' },
  { id: 'LEARNER', label: 'Learner' },
]

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  height: '100%',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  borderRadius: theme.spacing(1),
  boxShadow: selected ? '0 8px 22px rgba(37, 99, 235, 0.14)' : '0 2px 8px rgba(0,0,0,0.06)',
  border: `1px solid ${selected ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.divider, 0.9)}`,
  background: selected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}))

const TileIconContainer = styled(Box)(({ theme, color = 'primary.main' }) => ({
  width: 44,
  height: 44,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main, 0.1),
  color: color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main,
}))

const SectionPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)',
}))

const EmptyPanel = ({ icon, title, body, action }) => (
  <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
    <Box sx={{ mb: 1.5, color: 'text.disabled' }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
      {body}
    </Typography>
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
)

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeId(value) {
  return trimValue(value)
}

function normalizeList(values = []) {
  if (typeof values === 'string') {
    return values.split(',').map(normalizeId).filter(Boolean)
  }
  return Array.isArray(values) ? values.map(normalizeId).filter(Boolean) : []
}

function displayTitle(entity, fallback = 'Unknown') {
  return (
    entity?.title ||
    entity?.name ||
    entity?.displayName ||
    entity?.fullName ||
    entity?.id ||
    fallback
  )
}

function getInitials(label) {
  const parts = trimValue(label).split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('')
}

function avatarSrc(entity) {
  return entity?.profilePicId ? getImageLink(entity.profilePicId) : undefined
}

function auditorAvatar(entity, size = 24) {
  const label = displayTitle(entity, entity?.name || 'Auditor')
  return (
    <Avatar src={avatarSrc(entity)} sx={{ width: size, height: size, fontSize: size <= 24 ? 11 : 13 }}>
      {getInitials(label)}
    </Avatar>
  )
}

function getPractitionerType(detail, practitioner) {
  return trimValue(detail?.practitionerType || practitioner?.subtitle || practitioner?.type || 'therapist')
}

function getPractitionerRoleIds(detail, practitioner) {
  const serviceRoles = Object.keys(
    practitioner?.teacherUnEditableFields?.servicesApproved ||
      detail?.practitioner?.teacherUnEditableFields?.servicesApproved ||
      {},
  )
  const authorityRoles = [
    ...(Array.isArray(practitioner?.authorities) ? practitioner.authorities : []),
    ...(Array.isArray(detail?.user?.authorities) ? detail.user.authorities : []),
    ...(Array.isArray(practitioner?.user?.authorities) ? practitioner.user.authorities : []),
  ]
  return [...new Set([...serviceRoles, ...authorityRoles].map(trimValue).filter(Boolean))]
}

function buildEntityMap(items = []) {
  const map = new Map()
  items.forEach((item) => {
    if (!item) return
    ;[item.id, item.uid, item.userId, item.slug].map(normalizeId).filter(Boolean).forEach((id) => {
      if (!map.has(id)) map.set(id, item)
    })
  })
  return map
}

function getRoomBrandId(room, assignmentByRoomId) {
  return (
    normalizeId(room?.clinicBrandId) ||
    normalizeId(room?.clinicBrand?.id) ||
    normalizeId(room?.clinicBrandData?.id) ||
    normalizeId(assignmentByRoomId.get(normalizeId(room?.id))?.clinicBrandId)
  )
}

function getRoomLocationId(room, assignmentByRoomId) {
  return (
    normalizeId(room?.clinicLocationId) ||
    normalizeId(room?.clinicId) ||
    normalizeId(room?.clinicLocation?.id) ||
    normalizeId(room?.clinicLocationData?.id) ||
    normalizeId(assignmentByRoomId.get(normalizeId(room?.id))?.clinicLocationId)
  )
}

function getRoomTitle(room, fallbackId) {
  return displayTitle(room, fallbackId || 'Chamber room')
}

function getBrandTitle(room, brand, fallbackId) {
  return (
    room?.clinicBrandName ||
    room?.clinicBrandTitle ||
    room?.clinicBrand?.title ||
    room?.clinicBrand?.name ||
    displayTitle(brand, fallbackId || 'Clinic brand')
  )
}

function getLocationTitle(room, location, fallbackId) {
  return (
    room?.clinicLocationName ||
    room?.clinicName ||
    room?.clinicLocationTitle ||
    room?.clinicLocation?.title ||
    room?.clinicLocation?.name ||
    displayTitle(location, fallbackId || 'Clinic location')
  )
}

function formatAccessType(value) {
  const normalized = trimValue(value || 'SUPERVISOR').toUpperCase()
  if (normalized === 'LEARNER') return 'Learner'
  if (normalized === 'SUPERVISOR') return 'Supervisor'
  return normalized
    .toLowerCase()
    .split(/[-_\s]+/)
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function collectDetailRooms(detail) {
  const rooms = []
  ;(detail?.clinicAssignments || []).forEach((assignment) => {
    ;(assignment?.chamberRooms || []).forEach((room) => {
      if (!room?.id) return
      rooms.push({
        ...room,
        clinicBrandId: room.clinicBrandId || assignment.clinicBrandId,
        clinicLocationId: room.clinicLocationId || assignment.clinicLocationId,
      })
    })
  })
  return rooms
}

function buildAssignmentByRoomId(detail) {
  const map = new Map()
  ;(detail?.clinicAssignments || []).forEach((assignment) => {
    ;(assignment?.chamberRooms || []).forEach((room) => {
      if (room?.id) map.set(room.id, assignment)
    })
  })
  return map
}

function buildRoomMap(bootstrap, detail, roomsWithAuditAccess) {
  const map = new Map()
  ;[
    ...(bootstrap?.chamberRooms || []),
    ...collectDetailRooms(detail),
    ...(roomsWithAuditAccess || []),
  ].forEach((room) => {
    if (room?.id) {
      map.set(room.id, { ...(map.get(room.id) || {}), ...room })
    }
  })
  return map
}

function finalizeGroupedRooms(groupsMap) {
  return Array.from(groupsMap.values()).map((brand) => ({
    ...brand,
    locations: Array.from(brand.locations.values()).map((location) => ({
      ...location,
      rooms: Array.from(location.rooms.values()),
    })),
  }))
}

function createRoomContext({ roomId, room, assignmentByRoomId, brandMap, locationMap }) {
  const brandId = getRoomBrandId(room, assignmentByRoomId)
  const locationId = getRoomLocationId(room, assignmentByRoomId)
  const brand = brandMap.get(brandId)
  const location = locationMap.get(locationId)
  return {
    brandId,
    locationId,
    roomId,
    brandTitle: getBrandTitle(room, brand, brandId),
    locationTitle: getLocationTitle(room, location, locationId),
    roomTitle: getRoomTitle(room, roomId),
    supportedEventTypes: getRoomEventTypes(room),
  }
}

function getRoomEventTypes(room) {
  return [
    ...normalizeList(room?.eventTypesForThisRoom),
    ...normalizeList(room?.primaryEventTypesForThisRoom),
    ...normalizeList(room?.otherEventTypesForThisRoom),
    ...normalizeList(room?.supportedEventTypes),
    ...normalizeList(room?.eventTypes),
  ].map((eventType) => eventType.toLowerCase())
}

function roomSupportsEventType(room, eventType) {
  return getRoomEventTypes(room).includes(trimValue(eventType).toLowerCase())
}

function auditorOptionSupportsRoom(option, room) {
  const roomEventTypes = normalizeList(room?.supportedEventTypes || getRoomEventTypes(room))
    .map((eventType) => eventType.toLowerCase())
  if (roomEventTypes.length === 0) return true
  const auditorEventTypes = normalizeList(option?.supportedEventTypes)
    .map((eventType) => eventType.toLowerCase())
  if (auditorEventTypes.length === 0) return false
  return auditorEventTypes.some((eventType) => roomEventTypes.includes(eventType))
}

function defaultAuditorTypeForOption(option, room) {
  const roomEventTypes = normalizeList(room?.supportedEventTypes || getRoomEventTypes(room))
    .map((eventType) => eventType.toLowerCase())
  const permissions = option?.preview?.eventTypePermissions || option?.auditorProfile?.eventTypePermissions || []
  const now = Date.now()
  const matchingPermission = permissions.find((permission) => {
    const eventType = trimValue(permission?.eventType).toLowerCase()
    const roleExpiry = permission?.roleExpiry ? new Date(permission.roleExpiry).getTime() : null
    return (
      permission?.enabled !== false &&
      eventType &&
      (roomEventTypes.length === 0 || roomEventTypes.includes(eventType)) &&
      (!roleExpiry || Number.isNaN(roleExpiry) || roleExpiry > now)
    )
  })
  return trimValue(matchingPermission?.defaultAuditorType || 'SUPERVISOR').toUpperCase()
}

function practitionerHasRole(detail, practitioner, role) {
  return getPractitionerRoleIds(detail, practitioner).includes(role)
}

function canManageAuditRoom({ userRoles, detail, practitioner, practitionerType, room }) {
  if (!Array.isArray(userRoles) || userRoles.length === 0) return false
  if (userRoles.includes(ROLE_ADMIN) || userRoles.includes(ROLE_AUDIT_ADMIN)) return true
  if (
    practitionerType === 'therapist' &&
    userRoles.includes(ROLE_THERAPIST_AUDIT_ADMIN) &&
    practitionerHasRole(detail, practitioner, ROLE_THERAPIST) &&
    roomSupportsEventType(room, EVENT_TYPE_THERAPY_SESSION)
  ) {
    return true
  }
  if (
    practitionerType === 'screener' &&
    userRoles.includes(ROLE_SCREENER_AUDIT_ADMIN) &&
    practitionerHasRole(detail, practitioner, ROLE_SCREENER) &&
    roomSupportsEventType(room, EVENT_TYPE_SCREENING)
  ) {
    return true
  }
  return false
}

function buildIncomingAuditorRows({
  practitionerId,
  bootstrap,
  detail,
  roomsWithAuditAccess,
}) {
  const rowsByRoomId = new Map()
  const practitionerMap = buildEntityMap([
    ...(bootstrap?.practitioners || []),
    ...(bootstrap?.auditorPractitioners || []),
    detail?.practitioner,
  ])
  const brandMap = buildEntityMap(bootstrap?.clinicBrands || [])
  const locationMap = buildEntityMap(bootstrap?.clinicLocations || [])
  const assignmentByRoomId = buildAssignmentByRoomId(detail)
  const roomMap = buildRoomMap(bootstrap, detail, roomsWithAuditAccess)

  // Backend-resolved auditor display data (name/photo) + role-missing flag, keyed by room+auditor
  // and (as a fallback) by auditor. Lets role-less auditors render with their real name/photo and a
  // red "role missing" treatment instead of a raw practitioner id.
  const incomingMetaByRoomAuditor = new Map()
  const incomingMetaByAuditor = new Map()
  ;(detail?.incomingAuditors || []).forEach((entry) => {
    const auditorId = normalizeId(entry?.auditorPractitionerId || entry?.auditorMentorId)
    if (!auditorId) return
    const meta = {
      name: entry?.auditorName || '',
      profilePicId: entry?.auditorProfilePicId || '',
      roleMissing: Boolean(entry?.roleMissing),
      roleMissingReason: entry?.roleMissingReason || '',
    }
    const roomId = normalizeId(entry?.chamberRoomId)
    if (roomId) incomingMetaByRoomAuditor.set(`${roomId}:${auditorId}`, meta)
    if (!incomingMetaByAuditor.has(auditorId)) incomingMetaByAuditor.set(auditorId, meta)
  })

  function auditorMetaFor(roomId, auditorId) {
    return incomingMetaByRoomAuditor.get(`${roomId}:${auditorId}`) || incomingMetaByAuditor.get(auditorId)
  }

  function ensureRow(roomId, room = null) {
    if (!roomId) return null
    const resolvedRoom = room || roomMap.get(roomId) || { id: roomId, title: roomId }
    const roomContext = createRoomContext({
      roomId,
      room: resolvedRoom,
      assignmentByRoomId,
      brandMap,
      locationMap,
    })
    if (!rowsByRoomId.has(roomId)) {
      rowsByRoomId.set(roomId, {
        ...roomContext,
        id: roomId,
        room: resolvedRoom,
        auditors: [],
        temporaryAuditors: [],
      })
    }
    return rowsByRoomId.get(roomId)
  }

  function addPermanentAuditor(row, auditorId, type = 'SUPERVISOR') {
    if (!row || !auditorId) return
    const meta = auditorMetaFor(row.roomId || row.id, auditorId)
    const auditor = practitionerMap.get(auditorId)
    const resolvedName = meta?.name || displayTitle(auditor, auditorId)
    const resolvedProfilePicId = meta?.profilePicId || auditor?.profilePicId || ''
    const roleMissing = Boolean(meta?.roleMissing)
    const roleMissingReason = meta?.roleMissingReason || ''
    const existing = row.auditors.find((entry) => entry.id === auditorId)
    if (existing) {
      // Already added (e.g. from the chamber-room loop) — enrich it with backend-resolved data.
      if (meta) {
        existing.name = resolvedName
        existing.profilePicId = resolvedProfilePicId
        existing.roleMissing = roleMissing
        existing.roleMissingReason = roleMissingReason
      }
      return
    }
    row.auditors.push({
      id: auditorId,
      name: resolvedName,
      profilePicId: resolvedProfilePicId,
      type: 'Permanent',
      accessType: formatAccessType(type),
      rawType: trimValue(type || 'SUPERVISOR').toUpperCase(),
      expiresOn: '',
      roleMissing,
      roleMissingReason,
    })
  }

  function addTemporaryAuditor(row, auditorId, type = 'SUPERVISOR', expiresOn = '') {
    if (!row || !auditorId) return
    const key = `${auditorId}:${type}:${expiresOn}`
    if (row.temporaryAuditors.some((auditor) => `${auditor.id}:${auditor.rawType}:${auditor.expiresOn}` === key)) return
    const auditor = practitionerMap.get(auditorId)
    row.temporaryAuditors.push({
      id: auditorId,
      name: displayTitle(auditor, auditorId),
      profilePicId: auditor?.profilePicId || '',
      type: 'Temporary',
      accessType: formatAccessType(type),
      rawType: trimValue(type || 'SUPERVISOR').toUpperCase(),
      expiresOn,
    })
  }

  collectDetailRooms(detail).forEach((room) => {
    ensureRow(normalizeId(room?.id), room)
  })

  ;(roomsWithAuditAccess || []).forEach((room) => {
    const roomId = normalizeId(room?.id)
    const row = ensureRow(roomId, room)
    ;(room?.mentorsAndAuditors || []).forEach((combo) => {
      if (normalizeId(combo?.mentorId) !== practitionerId) return
      normalizeList(combo?.auditorIds).forEach((auditorId) => {
        addPermanentAuditor(row, auditorId, combo?.type || 'SUPERVISOR')
      })
      ;(combo?.auditAccessList || []).forEach((access) => {
        const auditorId = normalizeId(access?.auditorMentorId || access?.auditorPractitionerId)
        addTemporaryAuditor(
          row,
          auditorId,
          access?.type || combo?.type || 'SUPERVISOR',
          access?.expiresOn || access?.approvalExpiry || '',
        )
      })
    })
  })

  ;(detail?.incomingAuditors || []).forEach((entry) => {
    const roomId = normalizeId(entry?.chamberRoomId)
    const auditorId = normalizeId(entry?.auditorPractitionerId || entry?.auditorMentorId)
    if (!roomId || !auditorId) return
    const row = ensureRow(roomId)
    addPermanentAuditor(row, auditorId, entry?.type)
  })

  return Array.from(rowsByRoomId.values()).map((row) => ({
    ...row,
    auditors: row.auditors.sort((left, right) => left.name.localeCompare(right.name)),
    temporaryAuditors: row.temporaryAuditors.sort((left, right) => left.name.localeCompare(right.name)),
  }))
}

function buildIncomingAuditorGroups({ rows = [], includeEmpty = false, canShowEmptyRoom = () => false }) {
  const groupsMap = new Map()
  rows.forEach((row) => {
    const hasAuditors = row.auditors.length > 0 || row.temporaryAuditors.length > 0
    if (!hasAuditors && !(includeEmpty && canShowEmptyRoom(row))) return
    const brandKey = row.brandId || 'unknown-brand'
    const locationKey = row.locationId || 'unknown-location'
    const roomKey = row.roomId || 'unknown-room'

    if (!groupsMap.has(brandKey)) {
      groupsMap.set(brandKey, {
        id: brandKey,
        title: row.brandTitle,
        locations: new Map(),
      })
    }
    const brand = groupsMap.get(brandKey)
    if (!brand.locations.has(locationKey)) {
      brand.locations.set(locationKey, {
        id: locationKey,
        title: row.locationTitle,
        rooms: new Map(),
      })
    }
    const location = brand.locations.get(locationKey)
    if (!location.rooms.has(roomKey)) {
      location.rooms.set(roomKey, row)
    }
  })

  return finalizeGroupedRooms(groupsMap)
}

function buildAuditorDraftsByRoom(rows = []) {
  return rows.reduce((drafts, row) => {
    drafts[row.roomId] = row.auditors.map((auditor) => ({
      auditorPractitionerId: auditor.id,
      type: auditor.rawType || 'SUPERVISOR',
      name: auditor.name,
      profilePicId: auditor.profilePicId,
      roleMissing: auditor.roleMissing,
      roleMissingReason: auditor.roleMissingReason,
    }))
    return drafts
  }, {})
}

function normalizeDraftAuditors(auditors = []) {
  return auditors
    .map((auditor) => ({
      auditorPractitionerId: normalizeId(auditor?.auditorPractitionerId),
      type: trimValue(auditor?.type || 'SUPERVISOR').toUpperCase(),
    }))
    .filter((auditor) => auditor.auditorPractitionerId)
}

function draftMatchesRow(row, draftAuditors = []) {
  const rowKey = normalizeDraftAuditors(
    row.auditors.map((auditor) => ({
      auditorPractitionerId: auditor.id,
      type: auditor.rawType,
    })),
  )
    .map((auditor) => `${auditor.auditorPractitionerId}:${auditor.type}`)
    .sort()
    .join('|')
  const draftKey = normalizeDraftAuditors(draftAuditors)
    .map((auditor) => `${auditor.auditorPractitionerId}:${auditor.type}`)
    .sort()
    .join('|')
  return rowKey === draftKey
}

function buildOutgoingAuditCards({
  practitionerId,
  bootstrap,
  detail,
  roomsWithAuditAccess,
}) {
  const practitionerMap = buildEntityMap([
    ...(bootstrap?.practitioners || []),
    ...(bootstrap?.auditorPractitioners || []),
    detail?.practitioner,
  ])
  const brandMap = buildEntityMap(bootstrap?.clinicBrands || [])
  const locationMap = buildEntityMap(bootstrap?.clinicLocations || [])
  const assignmentByRoomId = buildAssignmentByRoomId(detail)
  const roomMap = buildRoomMap(bootstrap, detail, roomsWithAuditAccess)
  const cardsMap = new Map()

  function addPermission(mentorToAuditId, roomContext, meta = {}) {
    if (!mentorToAuditId) return
    const mentor = practitionerMap.get(mentorToAuditId)
    if (!cardsMap.has(mentorToAuditId)) {
      cardsMap.set(mentorToAuditId, {
        id: mentorToAuditId,
        name: displayTitle(mentor, mentorToAuditId),
        subtitle: mentor?.subtitle || mentor?.type || '',
        slug: mentor?.slug || '',
        rooms: [],
      })
    }
    cardsMap.get(mentorToAuditId).rooms.push({
      ...roomContext,
      accessType: formatAccessType(meta.accessType),
      type: meta.type || 'Permanent',
      expiresOn: meta.expiresOn || '',
    })
  }

  ;(roomsWithAuditAccess || []).forEach((room) => {
    const roomId = normalizeId(room?.id)
    const roomContext = createRoomContext({
      roomId,
      room,
      assignmentByRoomId,
      brandMap,
      locationMap,
    })
    ;(room?.mentorsAndAuditors || []).forEach((combo) => {
      const mentorToAuditId = normalizeId(combo?.mentorId)
      if (!mentorToAuditId || mentorToAuditId === practitionerId) return
      if (normalizeList(combo?.auditorIds).includes(practitionerId)) {
        addPermission(mentorToAuditId, roomContext, {
          accessType: combo?.type || 'SUPERVISOR',
          type: 'Permanent',
        })
      }
      ;(combo?.auditAccessList || []).forEach((access) => {
        const auditorId = normalizeId(access?.auditorMentorId || access?.auditorPractitionerId)
        if (auditorId !== practitionerId) return
        addPermission(mentorToAuditId, roomContext, {
          accessType: access?.type || combo?.type || 'SUPERVISOR',
          type: 'Temporary',
          expiresOn: access?.expiresOn || access?.approvalExpiry || '',
        })
      })
    })
  })

  const rawAssignments = [
    ...(detail?.auditorAssignments || []),
    ...(detail?.auditPermissions || []),
    ...(detail?.auditingPermissions || []),
  ]
  rawAssignments.forEach((assignment) => {
    const mentorToAuditId = normalizeId(
      assignment?.mentorId || assignment?.mentorToAuditId || assignment?.practitionerId,
    )
    const roomIds = normalizeList(assignment?.chamberRoomIds)
    if (roomIds.length === 0) {
      addPermission(
        mentorToAuditId,
        {
          brandId: 'all-brands',
          locationId: 'all-locations',
          roomId: 'all-rooms',
          brandTitle: 'All configured clinic brands',
          locationTitle: 'All configured locations',
          roomTitle: 'All rooms',
        },
        { accessType: assignment?.type, type: 'Permanent' },
      )
      return
    }
    roomIds.forEach((roomId) => {
      const room = roomMap.get(roomId) || { id: roomId, title: roomId }
      addPermission(
        mentorToAuditId,
        createRoomContext({
          roomId,
          room,
          assignmentByRoomId,
          brandMap,
          locationMap,
        }),
        { accessType: assignment?.type, type: 'Permanent' },
      )
    })
  })

  return Array.from(cardsMap.values()).map((card) => ({
    ...card,
    rooms: card.rooms.filter((room, index, rooms) => {
      const key = `${room.roomId}:${room.type}:${room.expiresOn}`
      return rooms.findIndex((candidate) => `${candidate.roomId}:${candidate.type}:${candidate.expiresOn}` === key) === index
    }),
  }))
}

export default function AuditPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const routeParams = useParams()

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const selectedSection = query.get('section') || ''
  // Slug from the human-readable path (/auditors/audit/<slug>); legacy query params are still
  // honoured so old links keep working.
  const scopedPractitionerSlug =
    normalizeId(routeParams.practitionerSlug) || normalizeId(query.get('practitionerSlug'))
  const scopedPractitionerId =
    normalizeId(query.get('practitionerId')) ||
    normalizeList(query.getAll('auditorMentorIds'))[0] ||
    ''
  const hasScopedPractitioner = Boolean(scopedPractitionerId || scopedPractitionerSlug)

  const [contextState, setContextState] = useState({
    loading: false,
    error: '',
    bootstrap: null,
    detail: null,
    roomsWithAuditAccess: [],
  })
  const [auditorsEditMode, setAuditorsEditMode] = useState(false)
  const [auditorDraftsByRoom, setAuditorDraftsByRoom] = useState({})
  const [savingAuditorRoomId, setSavingAuditorRoomId] = useState('')
  // Optimistically reveal the auditor sections right after a successful assign, before the
  // reloaded detail reflects the new authority.
  const [locallyGrantedAuditor, setLocallyGrantedAuditor] = useState(false)
  // Auditing Permissions (outgoing) section: the practitioners + rooms this auditor can cover.
  const [coverage, setCoverage] = useState({
    loading: false,
    error: '',
    targets: [],
    total: 0,
    eventTypes: [],
    page: 1,
    auditorRoles: [],
    selectedRole: '',
  })
  const [coverageSearch, setCoverageSearch] = useState('')
  const [coveragePage, setCoveragePage] = useState(1)
  const [coverageRole, setCoverageRole] = useState('')
  const [coverageSavingKey, setCoverageSavingKey] = useState('')
  const userRoles = useMemo(() => getUserRoles() || [], [])

  const loadPractitionerAuditContext = useCallback(async () => {
    if (!hasScopedPractitioner) return

    setContextState((prev) => ({ ...prev, loading: true, error: '' }))
    try {
      const [bootstrapResponse, detailResponse, roomsResponse] = await Promise.all([
        getPractitionerAuditBootstrap(),
        getPractitionerAuditDetail({
          practitionerId: scopedPractitionerId,
          practitionerSlug: scopedPractitionerSlug,
        }),
        getPractitionerAuditRooms({
          practitionerId: scopedPractitionerId,
          practitionerSlug: scopedPractitionerSlug,
        }),
      ])

      setContextState({
        loading: false,
        error: detailResponse.ok && roomsResponse.ok
          ? ''
          : detailResponse.message || roomsResponse.message || 'Unable to load practitioner audit profile.',
        bootstrap: bootstrapResponse.ok ? bootstrapResponse.data : null,
        detail: detailResponse.ok ? detailResponse.data : null,
        roomsWithAuditAccess: roomsResponse.ok ? roomsResponse.data : [],
      })
    } catch (error) {
      console.error('Error loading practitioner audit context:', error)
      setContextState({
        loading: false,
        error: 'Unable to load practitioner audit profile.',
        bootstrap: null,
        detail: null,
        roomsWithAuditAccess: [],
      })
    }
  }, [hasScopedPractitioner, scopedPractitionerId, scopedPractitionerSlug])

  useEffect(() => {
    loadPractitionerAuditContext()
  }, [loadPractitionerAuditContext])

  useEffect(() => {
    setLocallyGrantedAuditor(false)
    setCoverageRole('')
    setCoveragePage(1)
    setCoverageSearch('')
  }, [scopedPractitionerId, scopedPractitionerSlug])

  const practitioner = useMemo(() => {
    const fromDetail = contextState.detail?.practitioner
    if (fromDetail?.id || fromDetail?.slug) return fromDetail
    const practitionerMap = buildEntityMap(contextState.bootstrap?.practitioners || [])
    return practitionerMap.get(scopedPractitionerId) || practitionerMap.get(scopedPractitionerSlug) || null
  }, [contextState.bootstrap, contextState.detail, scopedPractitionerId, scopedPractitionerSlug])

  const practitionerType = getPractitionerType(contextState.detail, practitioner)
  const practitionerId = normalizeId(practitioner?.id || scopedPractitionerId)
  const practitionerName = displayTitle(practitioner, scopedPractitionerId || scopedPractitionerSlug || 'Practitioner')

  // Whether the scoped practitioner already holds an auditor role — drives both the section
  // gating (the three relationship sections only matter for an actual auditor) and the
  // assign/update copy. Derived from the same authorities getPractitionerType reads.
  const practitionerRoleIds = useMemo(
    () => getPractitionerRoleIds(contextState.detail, practitioner),
    [contextState.detail, practitioner],
  )
  const practitionerHasAuditorRole =
    practitionerRoleIds.includes(ROLE_THERAPY_AUDITOR) ||
    practitionerRoleIds.includes(ROLE_SCREENING_AUDITOR) ||
    locallyGrantedAuditor
  const eligibleAuditorRoles = useMemo(() => {
    const roles = []
    if (practitionerRoleIds.includes(ROLE_THERAPIST)) roles.push(ROLE_THERAPY_AUDITOR)
    if (practitionerRoleIds.includes(ROLE_SCREENER)) roles.push(ROLE_SCREENING_AUDITOR)
    if (roles.length === 0) {
      roles.push(practitionerType === 'screener' ? ROLE_SCREENING_AUDITOR : ROLE_THERAPY_AUDITOR)
    }
    return roles
  }, [practitionerRoleIds, practitionerType])
  const scopedRoleParam = (query.get('role') || '').trim().toUpperCase()
  const assignAllowedRoleIds = useMemo(() => {
    if ([ROLE_THERAPY_AUDITOR, ROLE_SCREENING_AUDITOR].includes(scopedRoleParam)) {
      return [scopedRoleParam]
    }
    return eligibleAuditorRoles
  }, [scopedRoleParam, eligibleAuditorRoles])
  const canManageAuditorRoles =
    userRoles.includes(ROLE_THERAPIST_ADMIN) || userRoles.includes(ROLE_ADMIN)

  const loadCoverage = useCallback(
    async ({ search = '', page = 1, role = '' } = {}) => {
      if (!practitionerId) return
      setCoverage((prev) => ({ ...prev, loading: true, error: '' }))
      const result = await getAuditableTargets({
        auditorPractitionerId: practitionerId,
        role,
        search,
        page,
        size: COVERAGE_PAGE_SIZE,
      })
      if (result.ok) {
        const targets = (result.data.targets || []).map((target) => ({
          ...target,
          rooms: (target.rooms || []).map((room) => ({
            ...room,
            type: room.type || target.type || 'SUPERVISOR',
          })),
        }))
        setCoverage({
          loading: false,
          error: '',
          targets,
          total: result.data.total || 0,
          eventTypes: result.data.auditorEventTypes || [],
          page: result.data.page || page,
          auditorRoles: result.data.auditorRoles || [],
          selectedRole: result.data.selectedRole || '',
        })
      } else {
        setCoverage({
          loading: false,
          error: result.message,
          targets: [],
          total: 0,
          eventTypes: [],
          page: 1,
          auditorRoles: [],
          selectedRole: '',
        })
      }
    },
    [practitionerId],
  )

  useEffect(() => {
    if (selectedSection !== 'auditing-permissions' || !practitionerId) return undefined
    const timer = setTimeout(() => {
      loadCoverage({ search: coverageSearch, page: coveragePage, role: coverageRole || scopedRoleParam })
    }, 300)
    return () => clearTimeout(timer)
  }, [selectedSection, practitionerId, coverageSearch, coveragePage, coverageRole, scopedRoleParam, loadCoverage])

  function updateCoverageRoom(targetId, roomId, changes) {
    setCoverage((prev) => ({
      ...prev,
      targets: prev.targets.map((target) => {
        if (target.practitionerId !== targetId) return target
        const rooms = target.rooms.map((room) =>
          room.chamberRoomId !== roomId ? room : { ...room, ...changes },
        )
        return { ...target, rooms, attachedRoomCount: rooms.filter((room) => room.attached).length }
      }),
    }))
  }

  async function toggleCoverageRoom(target, room) {
    const key = `${target.practitionerId}:${room.chamberRoomId}`
    setCoverageSavingKey(key)
    const payload = {
      auditorPractitionerId: practitionerId,
      mentorPractitionerId: target.practitionerId,
      chamberRoomId: room.chamberRoomId,
    }
    const result = room.attached
      ? await detachOutgoingAuditor(payload)
      : await attachOutgoingAuditor({ ...payload, type: room.type || 'SUPERVISOR' })
    if (result.ok) {
      updateCoverageRoom(target.practitionerId, room.chamberRoomId, { attached: !room.attached })
      toast.success(room.attached ? `Detached from ${room.roomTitle}` : `Attached to ${room.roomTitle}`)
    } else {
      toast.error(result.message)
    }
    setCoverageSavingKey('')
  }

  async function changeCoverageRoomType(target, room, type) {
    updateCoverageRoom(target.practitionerId, room.chamberRoomId, { type })
    if (!room.attached) return
    const key = `${target.practitionerId}:${room.chamberRoomId}`
    setCoverageSavingKey(key)
    const result = await attachOutgoingAuditor({
      auditorPractitionerId: practitionerId,
      mentorPractitionerId: target.practitionerId,
      chamberRoomId: room.chamberRoomId,
      type,
    })
    if (result.ok) {
      toast.success('Auditor type updated')
    } else {
      toast.error(result.message)
    }
    setCoverageSavingKey('')
  }

  // Resolve clinic brand / location titles from the loaded bootstrap (the room's denormalized
  // names are often empty, which is why only IDs were showing).
  const coverageBrandMap = useMemo(
    () => buildEntityMap(contextState.bootstrap?.clinicBrands || []),
    [contextState.bootstrap],
  )
  const coverageLocationMap = useMemo(
    () => buildEntityMap(contextState.bootstrap?.clinicLocations || []),
    [contextState.bootstrap],
  )

  function resolveCoverageBrandTitle(room) {
    const brand = coverageBrandMap.get(room.clinicBrandId)
    if (brand) return displayTitle(brand, room.brandTitle || room.clinicBrandId)
    return room.brandTitle || room.clinicBrandId || ''
  }

  function resolveCoverageLocationTitle(room) {
    const clinicLocation = coverageLocationMap.get(room.clinicLocationId)
    if (clinicLocation) return displayTitle(clinicLocation, room.locationTitle || room.clinicLocationId)
    return room.locationTitle || room.clinicLocationId || ''
  }
  const auditorRoomRows = useMemo(
    () =>
      practitionerId
        ? buildIncomingAuditorRows({
            practitionerId,
            bootstrap: contextState.bootstrap,
            detail: contextState.detail,
            roomsWithAuditAccess: contextState.roomsWithAuditAccess,
          })
        : [],
    [contextState.bootstrap, contextState.detail, contextState.roomsWithAuditAccess, practitionerId],
  )
  const canManageAuditorRow = useCallback(
    (row) =>
      canManageAuditRoom({
        userRoles,
        detail: contextState.detail,
        practitioner,
        practitionerType,
        room: row?.room,
      }),
    [contextState.detail, practitioner, practitionerType, userRoles],
  )
  const incomingGroups = useMemo(
    () =>
      buildIncomingAuditorGroups({
        rows: auditorRoomRows,
        includeEmpty: auditorsEditMode,
        canShowEmptyRoom: canManageAuditorRow,
      }),
    [auditorRoomRows, auditorsEditMode, canManageAuditorRow],
  )
  const canManageAnyAuditorRoom = useMemo(
    () => auditorRoomRows.some((row) => canManageAuditorRow(row)),
    [auditorRoomRows, canManageAuditorRow],
  )
  const outgoingCards = useMemo(
    () =>
      practitionerId
        ? buildOutgoingAuditCards({
            practitionerId,
            bootstrap: contextState.bootstrap,
            detail: contextState.detail,
            roomsWithAuditAccess: contextState.roomsWithAuditAccess,
          })
        : [],
    [contextState.bootstrap, contextState.detail, contextState.roomsWithAuditAccess, practitionerId],
  )

  useEffect(() => {
    if (!auditorsEditMode) return
    setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows))
  }, [auditorRoomRows, auditorsEditMode])

  function enterAuditorsEditMode() {
    setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows))
    setAuditorsEditMode(true)
  }

  function exitAuditorsEditMode() {
    setAuditorsEditMode(false)
    setAuditorDraftsByRoom({})
    setSavingAuditorRoomId('')
  }

  function setRoomDraft(roomId, updater) {
    setAuditorDraftsByRoom((prev) => {
      const current = normalizeDraftAuditors(prev[roomId] || [])
      return {
        ...prev,
        [roomId]: normalizeDraftAuditors(typeof updater === 'function' ? updater(current) : updater),
      }
    })
  }

  function addAuditorToRoomDraft(roomId, practitionerOption) {
    const auditorPractitionerId = normalizeId(practitionerOption?.id)
    if (!auditorPractitionerId) return
    const room = auditorRoomRows.find((row) => row.roomId === roomId)
    const defaultType = defaultAuditorTypeForOption(practitionerOption, room)
    setRoomDraft(roomId, (current) => {
      if (current.some((auditor) => auditor.auditorPractitionerId === auditorPractitionerId)) {
        return current
      }
      return [...current, { auditorPractitionerId, type: defaultType }]
    })
  }

  function updateAuditorTypeForRoom(roomId, auditorPractitionerId, type) {
    setRoomDraft(roomId, (current) =>
      current.map((auditor) =>
        auditor.auditorPractitionerId === auditorPractitionerId
          ? { ...auditor, type: trimValue(type || 'SUPERVISOR').toUpperCase() }
          : auditor,
      ),
    )
  }

  function removeAuditorFromRoomDraft(roomId, auditorPractitionerId) {
    setRoomDraft(roomId, (current) =>
      current.filter((auditor) => auditor.auditorPractitionerId !== auditorPractitionerId),
    )
  }

  function resetRoomDraft(row) {
    setRoomDraft(
      row.roomId,
      row.auditors.map((auditor) => ({
        auditorPractitionerId: auditor.id,
        type: auditor.rawType || 'SUPERVISOR',
      })),
    )
  }

  async function saveRoomAuditors(row) {
    if (!canManageAuditorRow(row)) {
      toast.error('Your audit role cannot manage this chamber room.')
      return
    }
    const draftAuditors = normalizeDraftAuditors(auditorDraftsByRoom[row.roomId] || [])
    setSavingAuditorRoomId(row.roomId)
    try {
      const response = await savePractitionerRoomAuditors({
        practitionerId,
        practitionerSlug: practitioner?.slug || scopedPractitionerSlug,
        chamberRoomId: row.roomId,
        auditors: draftAuditors.map((auditor) => ({
          chamberRoomId: row.roomId,
          auditorPractitionerId: auditor.auditorPractitionerId,
          type: auditor.type,
        })),
      })
      if (!response.ok || !response.data) {
        toast.error(response.message || 'Unable to save chamber-room auditors.')
        return
      }
      setContextState((prev) => ({
        ...prev,
        detail: response.data,
      }))
      toast.success('Room auditors saved.')
    } catch (error) {
      console.error('Error saving room auditors:', error)
      toast.error('Unable to save chamber-room auditors.')
    } finally {
      setSavingAuditorRoomId('')
    }
  }

  function updateSection(nextSection) {
    const params = new URLSearchParams(location.search)
    if (nextSection) params.set('section', nextSection)
    else params.delete('section')
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    })
  }

  function buildOnboardingUrl(section) {
    const params = new URLSearchParams()
    if (practitionerType) params.set('type', practitionerType)
    if (practitionerId) params.set('practitioner-id', practitionerId)
    if (practitioner?.slug) params.set('practitioner-slug', practitioner.slug)
    if (section) params.set('section', section)
    const basePath =
      section === 'auditor-access'
        ? '/support-home/practitioner-management/onboarding/auditor'
        : '/support-home/practitioner-management/onboarding'
    return `${basePath}?${params.toString()}`
  }

  const auditorProfileActions = [
    {
      id: 'audit-requests',
      title: 'Audit Requests',
      description: hasScopedPractitioner
        ? 'Requests this practitioner raised to audit other chamber rooms.'
        : 'Review and approve chamber-room audit requests.',
      icon: <Assignment />,
      countLabel: null,
    },
    {
      id: 'auditors',
      title: 'Auditors',
      description: 'People who can audit this practitioner, grouped by clinic, location, and room.',
      icon: <ManageAccounts />,
      countLabel: hasScopedPractitioner ? `${incomingGroups.length} clinic group${incomingGroups.length === 1 ? '' : 's'}` : null,
    },
    {
      id: 'auditing-permissions',
      title: 'Auditing Permissions',
      description: 'Other practitioners this person can audit across chamber rooms.',
      icon: <FactCheck />,
      countLabel: hasScopedPractitioner ? `${outgoingCards.length} practitioner${outgoingCards.length === 1 ? '' : 's'}` : null,
    },
  ]
  const assignAuditorAction = {
    id: ASSIGN_AUDITOR_SECTION,
    title: 'Assign Auditor Role',
    description: practitionerHasAuditorRole
      ? "Update this practitioner's auditor roles and event permissions."
      : 'Grant this practitioner an auditor role and event permissions.',
    icon: <HowToReg />,
    countLabel: null,
  }
  // When scoped to a practitioner, the three relationship sections only make sense once they
  // hold an auditor role; before that only "Assign Auditor Role" is offered (to grant one).
  const actions = (() => {
    if (!hasScopedPractitioner) return auditorProfileActions
    const list = []
    if (practitionerHasAuditorRole) list.push(...auditorProfileActions)
    if (canManageAuditorRoles) list.push(assignAuditorAction)
    return list
  })()

  function renderScopedHeader() {
    if (!hasScopedPractitioner) return null
    return (
      <SectionPaper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
              <Person />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {practitionerName}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={practitionerType || 'practitioner'} variant="outlined" />
                {practitionerId && <Chip size="small" label={practitionerId} variant="outlined" />}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadPractitionerAuditContext}
              disabled={contextState.loading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(buildOnboardingUrl('auditor-access'))}
              disabled={!practitionerId}
            >
              Edit Audit Access
            </Button>
          </Stack>
        </Stack>
      </SectionPaper>
    )
  }

  function renderActionTiles() {
    return (
      <Grid container spacing={2}>
        {actions.map((action) => (
          <Grid item xs={12} md={4} key={action.id}>
            <StyledCard selected={selectedSection === action.id} onClick={() => updateSection(action.id)}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <TileIconContainer color={action.id === 'audit-requests' ? 'primary.main' : 'secondary'}>
                      {React.cloneElement(action.icon, { fontSize: 'small' })}
                    </TileIconContainer>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {action.title}
                      </Typography>
                      {action.countLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {action.countLabel}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    )
  }

  function renderAuditorsSection() {
    if (!hasScopedPractitioner) {
      return (
        <Alert severity="info">
          Pick this from a practitioner card to see who audits that practitioner.
        </Alert>
      )
    }
    if (contextState.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )
    }
    const practitionerOptions =
      contextState.bootstrap?.auditorPractitioners ||
      contextState.bootstrap?.practitioners ||
      []
    return (
      <SectionPaper sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Auditors For {practitionerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Grouped as clinic brand, clinic location, and chamber room.
            </Typography>
          </Box>
          {canManageAnyAuditorRoom && (
            <Stack direction="row" spacing={1}>
              {auditorsEditMode && (
                <Button variant="outlined" startIcon={<Close />} onClick={exitAuditorsEditMode}>
                  Done
                </Button>
              )}
              <Button
                variant={auditorsEditMode ? 'outlined' : 'contained'}
                startIcon={<Edit />}
                onClick={auditorsEditMode ? () => setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows)) : enterAuditorsEditMode}
                disabled={!practitionerId}
              >
                {auditorsEditMode ? 'Reset Drafts' : 'Manage Per-Room Auditors'}
              </Button>
            </Stack>
          )}
        </Stack>

        {incomingGroups.length === 0 ? (
          <EmptyPanel
            icon={<Security sx={{ fontSize: 44 }} />}
            title={auditorsEditMode ? 'No rooms in your audit scope' : 'No auditors configured'}
            body={
              auditorsEditMode
                ? 'There are no chamber rooms available for your audit role scope on this practitioner.'
                : 'This practitioner does not currently have room-level auditors in the loaded clinic-room access data.'
            }
            action={
              canManageAnyAuditorRoom && !auditorsEditMode ? (
                <Button variant="outlined" startIcon={<Edit />} onClick={enterAuditorsEditMode}>
                  Manage Per-Room Auditors
                </Button>
              ) : null
            }
          />
        ) : (
          <Stack spacing={2}>
            {incomingGroups.map((brand) => (
              <Paper key={brand.id} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Business color="primary" fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {brand.title}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {brand.locations.map((clinicLocation) => (
                    <Box key={`${brand.id}-${clinicLocation.id}`}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                        <LocationOn color="action" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {clinicLocation.title}
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        {clinicLocation.rooms.map((room) => {
                            const roomCanManage = canManageAuditorRow(room)
                            const draftAuditors = normalizeDraftAuditors(auditorDraftsByRoom[room.roomId] || [])
                            const draftIds = new Set(draftAuditors.map((auditor) => auditor.auditorPractitionerId))
                            const candidateOptions = practitionerOptions
                              .filter(
                                (option) =>
                                  option?.id &&
                                  option.id !== practitionerId &&
                                  !draftIds.has(option.id),
                              )
                              .filter((option) => auditorOptionSupportsRoom(option, room))
                            const hasAuditors = room.auditors.length > 0 || room.temporaryAuditors.length > 0
                            const dirty = !draftMatchesRow(room, draftAuditors)
                            return (
                              <Paper
                                key={`${clinicLocation.id}-${room.roomId}`}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 1, bgcolor: hasAuditors ? 'rgba(248, 250, 252, 0.9)' : 'rgba(255, 251, 235, 0.75)' }}
                              >
                                <Stack spacing={1.25}>
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <MeetingRoom fontSize="small" color="action" />
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                          {room.roomTitle}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {room.roomId}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
                                      {!hasAuditors && auditorsEditMode && (
                                        <Chip size="small" color="warning" variant="outlined" label="No auditors" />
                                      )}
                                      {!roomCanManage && auditorsEditMode && (
                                        <Chip size="small" variant="outlined" label="Outside your audit scope" />
                                      )}
                                      {!auditorsEditMode &&
                                        room.auditors.map((auditor) =>
                                          auditor.roleMissing ? (
                                            <Box
                                              key={`${room.roomId}-${auditor.id}-${auditor.type}-${auditor.expiresOn}`}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                border: '1px solid #f1b0b0',
                                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                              }}
                                            >
                                              {auditorAvatar(auditor)}
                                              <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ display: 'block', fontWeight: 700, color: '#b91c1c', lineHeight: 1.2 }}
                                                >
                                                  {auditor.name} · {auditor.accessType}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ display: 'block', color: '#b91c1c', lineHeight: 1.2 }}
                                                >
                                                  {auditor.roleMissingReason || 'Auditor role missing'}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Chip
                                              key={`${room.roomId}-${auditor.id}-${auditor.type}-${auditor.expiresOn}`}
                                              size="small"
                                              color="primary"
                                              avatar={auditorAvatar(auditor)}
                                              label={`${auditor.name} - ${auditor.accessType}`}
                                            />
                                          ),
                                        )}
                                      {!auditorsEditMode && room.temporaryAuditors.map((auditor) => (
                                        <Chip
                                          key={`${room.roomId}-${auditor.id}-${auditor.type}-${auditor.expiresOn}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                          avatar={auditorAvatar(auditor)}
                                          label={`${auditor.name} - ${auditor.accessType}${auditor.expiresOn ? ` - expires ${formatDateTime(auditor.expiresOn)}` : ''}`}
                                        />
                                      ))}
                                    </Stack>
                                  </Stack>

                                  {auditorsEditMode && (
                                    <Stack spacing={1.25}>
                                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                        {draftAuditors.length === 0 ? (
                                          <Typography variant="caption" color="text.secondary">
                                            No permanent auditors assigned for this room.
                                          </Typography>
                                        ) : (
                                          draftAuditors.map((draftAuditor) => {
                                            const optionAuditor = practitionerOptions.find((option) => option.id === draftAuditor.auditorPractitionerId)
                                            // Fall back to the backend-resolved name/photo carried on the draft so role-less
                                            // auditors (absent from practitionerOptions) still show their real identity.
                                            const auditor = optionAuditor || {
                                              id: draftAuditor.auditorPractitionerId,
                                              name: draftAuditor.name,
                                              profilePicId: draftAuditor.profilePicId,
                                            }
                                            const roleMissing = Boolean(draftAuditor.roleMissing)
                                            return (
                                              <Stack
                                                key={`${room.roomId}-${draftAuditor.auditorPractitionerId}`}
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={0.75}
                                                alignItems={{ sm: 'center' }}
                                                sx={{
                                                  border: roleMissing ? '1px solid #f1b0b0' : '1px solid #dbe2ea',
                                                  borderRadius: 1,
                                                  px: 1,
                                                  py: 0.75,
                                                  bgcolor: roleMissing ? 'rgba(239, 68, 68, 0.1)' : 'background.paper',
                                                }}
                                              >
                                                {auditorAvatar(auditor, 28)}
                                                <Box sx={{ minWidth: 0 }}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, color: roleMissing ? '#b91c1c' : undefined }}
                                                  >
                                                    {draftAuditor.name || displayTitle(auditor, draftAuditor.auditorPractitionerId)}
                                                  </Typography>
                                                  {roleMissing && (
                                                    <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                                                      {draftAuditor.roleMissingReason || 'Auditor role missing'}
                                                    </Typography>
                                                  )}
                                                </Box>
                                                <TextField
                                                  select
                                                  size="small"
                                                  value={draftAuditor.type}
                                                  onChange={(event) =>
                                                    updateAuditorTypeForRoom(
                                                      room.roomId,
                                                      draftAuditor.auditorPractitionerId,
                                                      event.target.value,
                                                    )
                                                  }
                                                  disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                                  sx={{ minWidth: 132 }}
                                                >
                                                  {AUDITOR_TYPE_OPTIONS.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                      {option.label}
                                                    </MenuItem>
                                                  ))}
                                                </TextField>
                                                <Button
                                                  size="small"
                                                  color="error"
                                                  onClick={() =>
                                                    removeAuditorFromRoomDraft(
                                                      room.roomId,
                                                      draftAuditor.auditorPractitionerId,
                                                    )
                                                  }
                                                  disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                                >
                                                  Remove
                                                </Button>
                                              </Stack>
                                            )
                                          })
                                        )}
                                      </Stack>

                                      {room.temporaryAuditors.length > 0 && (
                                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                          {room.temporaryAuditors.map((auditor) => (
                                            <Chip
                                              key={`${room.roomId}-temp-${auditor.id}-${auditor.expiresOn}`}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                              avatar={auditorAvatar(auditor)}
                                              label={`${auditor.name} - request access${auditor.expiresOn ? ` - expires ${formatDateTime(auditor.expiresOn)}` : ''}`}
                                            />
                                          ))}
                                        </Stack>
                                      )}

                                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                                        <Autocomplete
                                          size="small"
                                          fullWidth
                                          options={candidateOptions}
                                          value={null}
                                          disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                          onChange={(_event, value) => addAuditorToRoomDraft(room.roomId, value)}
                                          getOptionLabel={(option) =>
                                            option?.subtitle
                                              ? `${displayTitle(option, option.id)} (${option.subtitle})`
                                              : displayTitle(option, option?.id || '')
                                          }
                                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label="Add auditor"
                                              placeholder="Search eligible auditor"
                                            />
                                          )}
                                          renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                              {auditorAvatar(option, 30)}
                                              <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                  {displayTitle(option, option?.id || '')}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {option?.subtitle || normalizeList(option?.supportedEventTypes).join(', ')}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}
                                        />
                                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', md: 'initial' }}>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => resetRoomDraft(room)}
                                            disabled={!dirty || savingAuditorRoomId === room.roomId}
                                          >
                                            Reset
                                          </Button>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={savingAuditorRoomId === room.roomId ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                            onClick={() => saveRoomAuditors(room)}
                                            disabled={!roomCanManage || !dirty || savingAuditorRoomId === room.roomId}
                                          >
                                            Save Room
                                          </Button>
                                        </Stack>
                                      </Stack>
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            )
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </SectionPaper>
    )
  }

  function renderAuditingPermissionsSection() {
    if (!hasScopedPractitioner) {
      return (
        <Alert severity="info">
          Pick this from a practitioner card to manage whom that practitioner can audit.
        </Alert>
      )
    }
    const eventTypeLabel = (coverage.eventTypes || []).join(', ')
    const totalPages = Math.max(1, Math.ceil((coverage.total || 0) / COVERAGE_PAGE_SIZE))
    return (
      <SectionPaper sx={{ p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Auditing Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Attach {practitionerName} as an auditor to practitioners whose chamber rooms support{' '}
            {eventTypeLabel ? <strong>{eventTypeLabel}</strong> : 'this auditor’s event types'}. Toggle
            per chamber room.
          </Typography>
          {coverage.auditorRoles.length > 1 ? (
            <TextField
              select
              size="small"
              label="Auditor role"
              value={coverageRole || coverage.selectedRole || ''}
              onChange={(event) => {
                setCoverageRole(event.target.value)
                setCoveragePage(1)
              }}
              sx={{ mt: 1.5, minWidth: 220 }}
            >
              {coverage.auditorRoles.map((roleId) => (
                <MenuItem key={roleId} value={roleId}>
                  {AUDITOR_ROLE_LABELS[roleId] || roleId}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            coverage.selectedRole && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 1 }}
                label={`As ${AUDITOR_ROLE_LABELS[coverage.selectedRole] || coverage.selectedRole}`}
              />
            )
          )}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search practitioners by name…"
          value={coverageSearch}
          onChange={(event) => {
            setCoverageSearch(event.target.value)
            setCoveragePage(1)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {coverage.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : coverage.error ? (
          <Alert severity="warning">{coverage.error}</Alert>
        ) : coverage.targets.length === 0 ? (
          <EmptyPanel
            icon={<FactCheck sx={{ fontSize: 44 }} />}
            title="No matching practitioners"
            body={
              coverageSearch
                ? 'No practitioners match your search in chamber rooms supporting this auditor’s event types.'
                : 'No chamber rooms currently support this auditor’s event types.'
            }
          />
        ) : (
          <Stack spacing={2}>
            {coverage.targets.map((target) => (
              <Paper key={target.practitionerId} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                  {auditorAvatar(target, 44)}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                      {target.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
                      {target.title && target.title !== target.name && (
                        <Chip size="small" label={target.title} variant="outlined" />
                      )}
                      <Chip size="small" label={target.practitionerId} variant="outlined" />
                      <Chip
                        size="small"
                        color={target.attachedRoomCount > 0 ? 'primary' : 'default'}
                        variant={target.attachedRoomCount > 0 ? 'filled' : 'outlined'}
                        label={`${target.attachedRoomCount} room${target.attachedRoomCount === 1 ? '' : 's'} attached`}
                      />
                    </Stack>
                  </Box>
                </Stack>
                <Divider sx={{ mb: 1.25 }} />
                <Stack spacing={1}>
                  {target.rooms.map((room) => {
                    const key = `${target.practitionerId}:${room.chamberRoomId}`
                    const saving = coverageSavingKey === key
                    return (
                      <Paper
                        key={room.chamberRoomId}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: room.attached ? 'rgba(236, 253, 245, 0.7)' : 'background.paper',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ md: 'center' }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <MeetingRoom fontSize="small" color="action" />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {room.roomTitle}
                              </Typography>
                              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                <Chip
                                  size="small"
                                  icon={<Business />}
                                  label={resolveCoverageBrandTitle(room)}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  icon={<LocationOn />}
                                  label={resolveCoverageLocationTitle(room)}
                                  variant="outlined"
                                />
                              </Stack>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-end' }}>
                            {!room.canManage && (
                              <Chip size="small" variant="outlined" label="Outside your scope" />
                            )}
                            <TextField
                              select
                              size="small"
                              label="Type"
                              value={room.type || 'SUPERVISOR'}
                              onChange={(event) => changeCoverageRoomType(target, room, event.target.value)}
                              disabled={!room.canManage || saving}
                              sx={{ minWidth: 132 }}
                            >
                              {AUDITOR_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            {saving ? (
                              <CircularProgress size={22} />
                            ) : (
                              <Tooltip title={room.attached ? 'Detach auditor' : 'Attach auditor'}>
                                <span>
                                  <Switch
                                    checked={room.attached}
                                    onChange={() => toggleCoverageRoom(target, room)}
                                    disabled={!room.canManage}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    )
                  })}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {totalPages > 1 && !coverage.loading && (
          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={coveragePage}
              onChange={(_event, value) => setCoveragePage(value)}
              size="small"
            />
          </Stack>
        )}
      </SectionPaper>
    )
  }

  function renderAssignAuditorRoleSection() {
    if (!canManageAuditorRoles) {
      return (
        <SectionPaper sx={{ p: 2.5 }}>
          <Alert severity="info">Assigning auditor roles requires Therapist Admin.</Alert>
        </SectionPaper>
      )
    }
    if (!practitionerId) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Open this from a practitioner to assign an auditor role.
        </Alert>
      )
    }
    return (
      <SectionPaper sx={{ p: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {practitionerHasAuditorRole ? 'Update Auditor Role' : 'Assign Auditor Role'} — {practitionerName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grant the relevant auditor role and configure event permissions. The practitioner becomes
          selectable as an auditor once saved.
        </Typography>
        <AuditorAssignmentForm
          fixedPractitioner={{
            practitionerId,
            id: practitionerId,
            slug: practitioner?.slug,
            name: practitionerName,
            userId: contextState.detail?.user?.userId,
          }}
          allowedRoleIds={assignAllowedRoleIds}
          onSuccess={() => {
            setLocallyGrantedAuditor(true)
            loadPractitionerAuditContext()
          }}
        />
      </SectionPaper>
    )
  }

  function renderSelectedSection() {
    if (selectedSection === ASSIGN_AUDITOR_SECTION) {
      return <Box sx={{ mt: 2 }}>{renderAssignAuditorRoleSection()}</Box>
    }
    if (!selectedSection) {
      if (hasScopedPractitioner && actions.length === 0) {
        return (
          <Alert severity="info" sx={{ mt: 2 }}>
            This practitioner does not have an auditor role yet, and you don&apos;t have permission to
            assign one.
          </Alert>
        )
      }
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Choose one of the audit actions above.
        </Alert>
      )
    }
    // The three relationship sections are only meaningful once the practitioner is an auditor.
    if (hasScopedPractitioner && !practitionerHasAuditorRole && AUDITOR_PROFILE_SECTIONS.includes(selectedSection)) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          This practitioner does not have an auditor role yet.{' '}
          {canManageAuditorRoles ? 'Use “Assign Auditor Role” to grant one.' : ''}
        </Alert>
      )
    }
    if (selectedSection === 'audit-requests') {
      return (
        <SectionPaper sx={{ mt: 2, overflow: 'hidden' }}>
          <AuditRequestsListing
            isFromTherapists={hasScopedPractitioner}
            scopedAuditorPractitionerId={practitionerId}
            embedded
          />
        </SectionPaper>
      )
    }
    if (selectedSection === 'auditors') {
      return <Box sx={{ mt: 2 }}>{renderAuditorsSection()}</Box>
    }
    if (selectedSection === 'auditing-permissions') {
      return <Box sx={{ mt: 2 }}>{renderAuditingPermissionsSection()}</Box>
    }
    return null
  }

  return (
    <>
      <SupportHeader 
        heading="Audit Management" 
        showBackButton 
        backPath={hasScopedPractitioner ? '/support-home/practitioner-management/therapists' : '/support-home/practitioner-management'}
      />
      <Box
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '24px', md: '32px' },
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {selectedSection && (
            <Tooltip title="Back to audit actions">
              <IconButton size="small" onClick={() => updateSection('')}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {hasScopedPractitioner ? 'Practitioner Audit' : 'Audit Management'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Requests, per-room auditors, and audit permissions in one place.
            </Typography>
          </Box>
        </Stack>

        {contextState.error && hasScopedPractitioner && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {contextState.error}
          </Alert>
        )}
        {renderScopedHeader()}
        {renderActionTiles()}
        {renderSelectedSection()}
      </Box>
    </>
  )
}
